import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as actions from '../store/productPurchaseOrderTodo.action';
import * as supplierAction from '../../../../Supplier/store/Supplier.action';
import * as colourAction from '../../../../Library/ColorLibary/store/ColorLibrary.action';
import * as SystemMastersAction from '../../../../SystemMasters/Store/SystemMasters.action';

const PACK_TYPE_SINGLE = 'single-packed';

export const calculateTotalPrice = (packType, priceAfterDiscount, quantity, ratio, packs) => {
  const price = Number(priceAfterDiscount || 0);

  if (packType === PACK_TYPE_SINGLE) {
    return Number((Number(quantity || 0) * price).toFixed(2));
  }

  return Number((Number(ratio || 0) * Number(packs || 0) * price).toFixed(2));
};

export const useProductPurchaseOrderTodo = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('purchase-order');

  const isSupplierLoggedIn = useSelector((state) => state.UserDetails?.isSupplierLoggedIn);
  const userInfo = useSelector((state) => state.UserDetails?.userInfo);
  const logInRights = useSelector((state) => state.Users?.logInRights);
  const allTierList = useSelector((state) => state.SuppliersDetails?.allTierList);
  const ColorLibList = useSelector((state) => state.ColorLibraryDetails?.ColorLibList);
  const lengthVisible = useSelector((state) => state.SystemMastersDetails?.lengthVisible);
  const { purchaseOrderTodos, totalCount, error, loading } = useSelector((state) => state.productPurchaseOrderTodoReducer || {});

  const [searchState, setSearchState] = useState({
    searchLoaded: false,
    searchText: '',
  });

  const [state, setState] = useState({
    purchaseOrderTodos: [],
    selectedIds: [],
    isSelectAll: false,
    expandAll: false,
    showRevisedColumns: true,
    showActualColumns: true,
    currentPage: 1,
    itemsPerPage: 20,
    totalItems: 0,
    hasMore: true,
    isInitialLoading: true,
    isTableLoading: false,
    visibleColumns: {},
    deletedId: [],
    deleteModal: false,
    deleteMultipleModal: false,
  });

  const [filterState, setFilterState] = useState({
    activeSupplier: [],
    activeColourways: [],
    activeStatus: [],
  });

  const [pendingUpdates, setPendingUpdates] = useState({});
  const debounceTimeoutRef = useRef(null);
  const currentlyProcessingRef = useRef(new Set());
  const pendingUpdatesRef = useRef({});
  const fetchRef = useRef(null);

  useEffect(() => {
    if (!loading && (state.isInitialLoading || state.isTableLoading)) {
      setState((prev) => ({
        ...prev,
        isInitialLoading: false,
        isTableLoading: false,
      }));
    }
  }, [loading, state.isInitialLoading, state.isTableLoading]);

  useEffect(() => {
    const todos = [];

    if (purchaseOrderTodos && purchaseOrderTodos.length > 0) {
      const currentPendingUpdates = pendingUpdatesRef.current;
      const transformedTodos = purchaseOrderTodos.map((order) => {
        const existingOrder = state.purchaseOrderTodos.find((o) => o._id === order._id);
        const pendingOrderUpdates = currentPendingUpdates[order._id];

        // Preserve isExpanded from current state if it exists
        const preservedIsExpanded = existingOrder?.isExpanded !== undefined ? existingOrder.isExpanded : false;

        // Merge pending updates with Redux data for parent row
        let mergedOrder = { ...order };
        if (pendingOrderUpdates) {
          const { childUpdates, ...parentUpdates } = pendingOrderUpdates;
          // Merge parent updates (excluding childUpdates) - these are the user's current edits
          mergedOrder = { ...mergedOrder, ...parentUpdates };
        }

        // Merge with existing local state for parent row actual fields if they differ from Redux
        // This preserves user edits that haven't been saved yet
        if (existingOrder) {
          const actualFields = ['actualTotalUnits', 'actualApprovedByDate', 'actualExFactoryDate', 'actualShipmentDate', 'actualTotalPrice'];
          actualFields.forEach((field) => {
            const localValue = existingOrder[field];
            const reduxValue = mergedOrder[field];
            // Preserve local value if it exists and differs from Redux (user has edited it)
            // Or if there's a pending update for this field
            const hasPendingUpdate = pendingOrderUpdates && pendingOrderUpdates[field] !== undefined;
            if ((localValue !== undefined && localValue !== null && localValue !== reduxValue) || hasPendingUpdate) {
              mergedOrder[field] = localValue;
            }
          });
        }

        let mergedExpandData = mergedOrder.expandData || [];
        if (existingOrder && Array.isArray(existingOrder.expandData) && existingOrder.expandData.length > 0) {
          const reduxExpandData = mergedOrder.expandData || [];
          const pendingChildUpdates = pendingOrderUpdates?.childUpdates || {};

          mergedExpandData = reduxExpandData.map((reduxExpandRow) => {
            const localExpandRow = existingOrder.expandData.find((localRow) => {
              const localDetail = localRow?.orderDetails?.[0] || {};
              const reduxDetail = reduxExpandRow?.orderDetails?.[0] || {};
              const localDetailId = localDetail?._id?.toString() || localRow?.orderDetailsId?.toString();
              const reduxDetailId = reduxDetail?._id?.toString() || reduxExpandRow?.orderDetailsId?.toString();
              return localDetailId === reduxDetailId;
            });

            const expandDetailId = reduxExpandRow?.orderDetails?.[0]?._id?.toString() || reduxExpandRow?.orderDetailsId?.toString();
            const pendingChildUpdate = expandDetailId ? pendingChildUpdates[expandDetailId] : null;

            if (localExpandRow || pendingChildUpdate) {
              const mergedRow = {
                ...reduxExpandRow,
                ...(localExpandRow || {}),
                ...(pendingChildUpdate || {}),
              };

              // Merge orderDetails
              if (localExpandRow?.orderDetails || pendingChildUpdate) {
                mergedRow.orderDetails = (localExpandRow?.orderDetails || reduxExpandRow.orderDetails || []).map((od) => {
                  const odId = od._id?.toString();
                  const localOd = localExpandRow?.orderDetails?.find((lod) => lod._id?.toString() === odId);
                  const pendingOd = pendingChildUpdate && odId === expandDetailId ? pendingChildUpdate : null;

                  if (localOd || pendingOd) {
                    return { ...od, ...(localOd || {}), ...(pendingOd || {}) };
                  }
                  return od;
                });
              }

              return mergedRow;
            }
            return reduxExpandRow;
          });
        } else if (pendingOrderUpdates?.childUpdates && Object.keys(pendingOrderUpdates.childUpdates).length > 0) {
          // If there are pending child updates but no existing expandData, preserve them
          mergedExpandData = existingOrder?.expandData || [];
        }

        return {
          ...mergedOrder,
          poRef: mergedOrder.poRef || mergedOrder.purchaseOrderNumber,
          supplier: mergedOrder.supplier || mergedOrder.supplierFactoryDetails?.supplierName || mergedOrder.supplierId?.supplierName || '',
          factory: mergedOrder.factory || mergedOrder.supplierFactoryDetails?.factoryName || mergedOrder.factoryId?.factories?.[0]?.factoryName || '',
          templateName: mergedOrder.templateName || mergedOrder.poTemplateId?.templateName || '',
          productNumber: mergedOrder.productNumber || mergedOrder.orderDetails?.[0]?.productNumber?.number || mergedOrder.devOrderId?.number || '',
          image: mergedOrder.image || mergedOrder.orderDetails?.[0]?.productNumber?.image || mergedOrder.companyLogo || '',
          brand: mergedOrder.brand || mergedOrder.orderDetails?.[0]?.productNumber?.brandId?.name || mergedOrder.devOrderId?.brandId?.name || '',
          season: mergedOrder.season || mergedOrder.orderDetails?.[0]?.productNumber?.seasonId?.name || mergedOrder.devOrderId?.seasonId?.name || '',
          month: mergedOrder.month || mergedOrder.orderDetails?.[0]?.productNumber?.month || mergedOrder.devOrderId?.month || '',
          year: mergedOrder.year || mergedOrder.orderDetails?.[0]?.productNumber?.year || mergedOrder.devOrderId?.year || '',
          category1: mergedOrder.category1 || mergedOrder.orderDetails?.[0]?.productNumber?.catOneId?.name || mergedOrder.devOrderId?.catOneId?.name || '',
          category2: mergedOrder.category2 || mergedOrder.orderDetails?.[0]?.productNumber?.catTwoId?.name || mergedOrder.devOrderId?.catTwoId?.name || '',
          category3: mergedOrder.category3 || mergedOrder.orderDetails?.[0]?.productNumber?.catThreeId?.name || mergedOrder.devOrderId?.catThreeId?.name || '',
          category4: mergedOrder.category4 || mergedOrder.orderDetails?.[0]?.productNumber?.catFourId?.name || mergedOrder.devOrderId?.catFourId?.name || '',
          packType: mergedOrder.packType || mergedOrder.orderDetails?.[0]?.packType || '',
          packRatio: mergedOrder.packRatio || mergedOrder.orderDetails?.[0]?.ratio || 0,
          totalPacks: mergedOrder.totalPacks || mergedOrder.orderDetails?.[0]?.packs || 0,
          totalUnits: mergedOrder.totalUnits || mergedOrder.orderDetails?.[0]?.quantity || 0,
          location: mergedOrder.location || mergedOrder.orderDetails?.[0]?.location || '',
          unitPrice: mergedOrder.unitPrice || mergedOrder.orderDetails?.[0]?.unitPrice || 0,
          discountPct: mergedOrder.discountPct || mergedOrder.orderDetails?.[0]?.discountPercent || 0,
          priceAfterDiscount: mergedOrder.priceAfterDiscount || mergedOrder.orderDetails?.[0]?.priceAfterDiscount || 0,
          totalPrice:
            mergedOrder.totalPrice ||
            calculateTotalPrice(
              mergedOrder.packType || mergedOrder.orderDetails?.[0]?.packType || '',
              mergedOrder.priceAfterDiscount || mergedOrder.orderDetails?.[0]?.priceAfterDiscount || 0,
              mergedOrder.totalUnits || mergedOrder.orderDetails?.[0]?.quantity || 0,
              mergedOrder.packRatio || mergedOrder.orderDetails?.[0]?.ratio || 0,
              mergedOrder.totalPacks || mergedOrder.orderDetails?.[0]?.packs || 0,
            ),
          originalTotalPrice:
            mergedOrder.originalTotal ||
            calculateTotalPrice(
              mergedOrder.packType || mergedOrder.orderDetails?.[0]?.packType || '',
              mergedOrder.priceAfterDiscount || mergedOrder.orderDetails?.[0]?.priceAfterDiscount || 0,
              mergedOrder.originalTotalUnits ||
                mergedOrder.orderDetails?.[0]?.originalQuantity ||
                mergedOrder.totalUnits ||
                mergedOrder.orderDetails?.[0]?.quantity ||
                0,
              mergedOrder.packRatio || mergedOrder.orderDetails?.[0]?.ratio || 0,
              mergedOrder.totalPacks || mergedOrder.orderDetails?.[0]?.packs || 0,
            ),
          approvedByDate: mergedOrder.approvedByDate || mergedOrder.orderDetails?.[0]?.approvedByDate || null,
          exFactoryDate: mergedOrder.exFactoryDate || mergedOrder.orderDetails?.[0]?.exFactoryDate || null,
          shipmentDate: mergedOrder.shipmentDate || mergedOrder.orderDetails?.[0]?.shipmentDate || null,
          originalTotalUnits: mergedOrder.originalTotalUnits || mergedOrder.orderDetails?.[0]?.originalQuantity || null,
          originalApprovedByDate: mergedOrder.originalApprovedByDate || mergedOrder.orderDetails?.[0]?.originalApprovedByDate || null,
          originalExFactoryDate: mergedOrder.originalExFactoryDate || mergedOrder.orderDetails?.[0]?.originalExFactoryDate || null,
          originalShipmentDate: mergedOrder.originalShipmentDate || mergedOrder.orderDetails?.[0]?.originalShipmentDate || null,
          originalTotal: mergedOrder.originalTotal || mergedOrder.orderDetails?.[0]?.originalTotal || null,
          actualTotalUnits: mergedOrder.actualTotalUnits ?? mergedOrder.orderDetails?.[0]?.actualTotalUnits ?? null,
          actualApprovedByDate: mergedOrder.actualApprovedByDate ?? mergedOrder.orderDetails?.[0]?.actualApprovedByDate ?? null,
          actualExFactoryDate: mergedOrder.actualExFactoryDate ?? mergedOrder.orderDetails?.[0]?.actualExFactoryDate ?? null,
          actualShipmentDate: mergedOrder.actualShipmentDate ?? mergedOrder.orderDetails?.[0]?.actualShipmentDate ?? null,
          actualTotalPrice:
            mergedOrder.actualTotalPrice ??
            (mergedOrder.orderDetails?.[0]?.actualTotalUnits && mergedOrder.orderDetails?.[0]?.priceAfterDiscount
              ? calculateTotalPrice(
                  mergedOrder.packType || mergedOrder.orderDetails?.[0]?.packType || '',
                  mergedOrder.orderDetails[0].priceAfterDiscount,
                  mergedOrder.orderDetails[0].actualTotalUnits,
                  mergedOrder.packRatio || mergedOrder.orderDetails?.[0]?.ratio || 0,
                  mergedOrder.totalPacks || mergedOrder.orderDetails?.[0]?.packs || 0,
                )
              : null),
          revisedTotalUnits: mergedOrder.revisedTotalUnits || mergedOrder.orderDetails?.[0]?.revisedQuantity || null,
          revisedApprovedByDate: mergedOrder.revisedApprovedByDate || mergedOrder.orderDetails?.[0]?.revisedApprovedByDate || null,
          revisedExFactoryDate: mergedOrder.revisedExFactoryDate || mergedOrder.orderDetails?.[0]?.revisedExFactoryDate || null,
          revisedShipmentDate: mergedOrder.revisedShipmentDate || mergedOrder.orderDetails?.[0]?.revisedShipmentDate || null,
          revisedTotalPrice: mergedOrder.revisedTotalPrice || mergedOrder.orderDetails?.[0]?.revisedTotal || null,
          colourway:
            mergedOrder.colourway ||
            (mergedOrder.orderDetails?.[0]?.colourway
              ? typeof mergedOrder.orderDetails[0].colourway === 'string'
                ? mergedOrder.orderDetails[0].colourway
                : `${mergedOrder.orderDetails[0].colourway.name || ''}${
                    mergedOrder.orderDetails[0].colourway.newReferences ? ` | ${mergedOrder.orderDetails[0].colourway.newReferences}` : ''
                  }`
              : ''),
          size:
            mergedOrder.size ||
            (mergedOrder.orderDetails?.[0]?.size
              ? typeof mergedOrder.orderDetails[0].size === 'string'
                ? mergedOrder.orderDetails[0].size
                : mergedOrder.orderDetails[0].size?.name || ''
              : ''),
          length:
            mergedOrder.length ||
            (mergedOrder.orderDetails?.[0]?.length
              ? typeof mergedOrder.orderDetails[0].length === 'string'
                ? mergedOrder.orderDetails[0].length
                : mergedOrder.orderDetails[0].length?.name || ''
              : ''),
          originalOrderDetails: mergedOrder.orderDetails || [],
          isExpanded: preservedIsExpanded,
          expandData:
            Array.isArray(mergedExpandData) && mergedExpandData.length > 0
              ? mergedExpandData.map((expandItem) => ({
                  ...expandItem,
                  totalPrice:
                    expandItem.totalPrice ||
                    calculateTotalPrice(
                      expandItem.packType || '',
                      expandItem.priceAfterDiscount || 0,
                      expandItem.totalUnits || expandItem.quantity || 0,
                      expandItem.packRatio || expandItem.ratio || 0,
                      expandItem.totalPacks || expandItem.packs || 0,
                    ),
                  revisedTotalPrice:
                    expandItem.revisedTotalPrice ||
                    (expandItem.revisedTotal
                      ? expandItem.revisedTotal
                      : expandItem.revisedTotalUnits && expandItem.priceAfterDiscount
                      ? calculateTotalPrice(
                          expandItem.packType || '',
                          expandItem.priceAfterDiscount,
                          expandItem.revisedTotalUnits || expandItem.revisedQuantity || 0,
                          expandItem.packRatio || expandItem.ratio || 0,
                          expandItem.totalPacks || expandItem.packs || 0,
                        )
                      : null),
                  actualTotalPrice:
                    expandItem.actualTotalPrice ||
                    (expandItem.actualTotalUnits && expandItem.priceAfterDiscount
                      ? calculateTotalPrice(
                          expandItem.packType || '',
                          expandItem.priceAfterDiscount,
                          expandItem.actualTotalUnits,
                          expandItem.packRatio || expandItem.ratio || 0,
                          expandItem.totalPacks || expandItem.packs || 0,
                        )
                      : null),
                }))
              : mergedOrder.expandData,
        };
      });
      todos.push(...transformedTodos);
    }

    setState((prev) => ({
      ...prev,
      purchaseOrderTodos: todos,
      totalItems: totalCount || 0,
    }));
  }, [purchaseOrderTodos, totalCount]);

  useEffect(() => {
    const currentPage = state.currentPage || 1;
    const itemsPerPage = state.itemsPerPage || 20;
    const totalItems = totalCount || 0;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const hasMore = currentPage * itemsPerPage < totalItems;

    setState((prev) => ({
      ...prev,
      totalItems,
      totalPages,
      hasMore,
    }));
  }, [state.currentPage, state.itemsPerPage, totalCount]);

  const fetchPurchaseOrderTodos = useCallback(
    async (params = {}) => {
      try {
        const isInitialLoad = !params.append && state.purchaseOrderTodos.length === 0;
        setState((prev) => ({
          ...prev,
          isInitialLoading: isInitialLoad,
          isTableLoading: !isInitialLoad,
        }));

        const queryParams = {
          pageIndex: params.pageIndex || 1,
          pageSize: params.pageSize || 20,
          searchText: params.searchText?.trim() || '',
          filters: {
            supplierIds: params.filters?.supplierIds || [],
            colourwayIds: params.filters?.colourwayIds || [],
            statuses: params.filters?.statuses || [],
          },
        };

        return await dispatch(actions.fetchPurchaseOrderTodosList(queryParams, t));
      } finally {
        setState((prev) => ({
          ...prev,
          isInitialLoading: false,
          isTableLoading: false,
        }));
      }
    },
    [dispatch, t],
  );

  const debouncedSave = useCallback(async () => {
    const latestPendingUpdates = pendingUpdatesRef.current;

    if (Object.keys(latestPendingUpdates).length === 0) {
      return;
    }

    try {
      const result = await dispatch(
        actions.batchUpdateProductPurchaseOrderTodoActualFields({
          updates: latestPendingUpdates,
          t,
        }),
      );

      if (result?.success) {
        setPendingUpdates({});
        pendingUpdatesRef.current = {};
      }
      currentlyProcessingRef.current.clear();
    } catch (updateError) {
      console.error('Error saving actual fields:', updateError);
      currentlyProcessingRef.current.clear();
    }
  }, [dispatch, t]);

  useEffect(() => {
    const fetchFilterData = async () => {
      if (userInfo?.userId) {
        await Promise.all([
          dispatch(supplierAction.fetchAllTierSupplierListAction()),
          dispatch(
            colourAction.fetchColorLibrary({
              loggedInUserId: userInfo?.userId,
            }),
          ),
          dispatch(SystemMastersAction.getLengthAccess()),
        ]);
      }
    };
    fetchFilterData();
  }, [dispatch, userInfo?.userId]);

  useEffect(() => {
    pendingUpdatesRef.current = pendingUpdates;
  }, [pendingUpdates]);

  useEffect(() => {
    fetchRef.current = fetchPurchaseOrderTodos;
  }, [fetchPurchaseOrderTodos]);

  return {
    state,
    setState,
    searchState,
    setSearchState,
    filterState,
    setFilterState,
    pendingUpdates,
    setPendingUpdates,
    isSupplierLoggedIn,
    userInfo,
    logInRights,
    allTierList,
    ColorLibList,
    lengthVisible,
    purchaseOrderTodos,
    totalCount,
    error,
    loading,
    fetchPurchaseOrderTodos,
    debouncedSave,
    debounceTimeoutRef,
    currentlyProcessingRef,
    pendingUpdatesRef,
    fetchRef,
  };
};

export default useProductPurchaseOrderTodo;
